import {
  convertToEndOfMonthUTC,
  convertToEndOfWeekUTC,
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  getStartOfCurrentMonthUTC,
  getStartOfCurrentWeekUTC,
  isSameMonth,
  startOfTodayUTC,
} from "~/utils/datetime"
import { calculateWeeksOnHandForMultipleDates } from "~/utils/weeksOnHand"
import { roundForecast } from "~/utils/formatting"
import {
  VariantsTableRow,
  type ProductTableData,
} from "~/components/product/VariantTableCard/types"
import type { DateQuantityMap, RowCalculationConfig, RowCalculationResult } from "./types"
import { roundQuantity } from "./timeSeriesProcessor"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"
import { type FiscalConfig, fiscalPeriodRangeForMonth } from "~/utils/fiscalCalendar"

/**
 * Whether a period precedes the first inventory record we have for the variant, so it should
 * render as "no data" — a dash in the ClosingStock/WeeksOnHand rows, and a gap (line start) in
 * the inventory graph's closing-stock series.
 * - `undefined` firstInventoryDate → caller didn't opt in; never dash (behaviour unchanged).
 * - `null` firstInventoryDate → no inventory records at all; dash every historic period.
 * - a date → dash periods strictly before it.
 * Future periods (start ≥ current period) are never dashed — they carry projections.
 */
export function isBeforeFirstKnownInventory(
  periodStart: Date,
  currentPeriodStart: Date,
  firstInventoryDate: Date | null | undefined,
): boolean {
  if (firstInventoryDate === undefined) return false
  const isHistoric = periodStart.getTime() < currentPeriodStart.getTime()
  if (firstInventoryDate === null) return isHistoric
  return periodStart.getTime() < firstInventoryDate.getTime()
}

/**
 * Days a Sales cell's value covers. On a fiscal calendar a monthly cell is keyed on the calendar
 * month but sums the fiscal period's whole weeks (`rebucketWeekRowsToFiscalMonths`).
 */
export function salesCellDateRange(
  dateRange: { start: Date; end: Date },
  monthlyView: boolean,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): { start: Date; end: Date } {
  if (!monthlyView || !fiscalConfig) return dateRange
  return fiscalPeriodRangeForMonth(dateRange.start, fiscalConfig)
}

/**
 * Sales Index formula: (current / benchmark) * 100.
 * Returns null when the benchmark is missing or zero so callers can flag no-comparison cases.
 */
function calculateSalesIndex(current: number, benchmark: number | null): number | null {
  if (benchmark == null || benchmark === 0) return null
  return Math.round((current / benchmark) * 100)
}

/** Transform a DateQuantityMap into a RowCalculationResult */
function transformMap(
  data: DateQuantityMap,
  transform: (dateMs: number, qty: number | null) => ProductTableData,
): RowCalculationResult {
  const result: RowCalculationResult = new Map()
  for (const [dateMs, qty] of data) {
    result.set(dateMs, transform(dateMs, qty))
  }
  return result
}

/**
 * Main calculation function that processes row data for both variant and SKU levels.
 * Expects rawData and secondaryData as DateQuantityMap (pre-processed by caller).
 */
export function calculateRowData(config: RowCalculationConfig): RowCalculationResult {
  const { rowType, rawData, monthlyView, secondaryData, stockData, forecastData } = config

  const getDateRange = (dateMs: number) => {
    const date = new Date(dateMs)
    return {
      start: monthlyView ? convertToStartOfMonthUTC(date) : convertToStartOfWeekUTC(date),
      end: monthlyView ? convertToEndOfMonthUTC(date) : convertToEndOfWeekUTC(date),
    }
  }

  switch (rowType) {
    case VariantsTableRow.Sales: {
      return transformMap(rawData, (dateMs, qty) => {
        const dateRange = getDateRange(dateMs)
        if (dateRange.start > startOfTodayUTC()) {
          return { value: null, dateRange }
        }

        return {
          value: roundQuantity(qty),
          dateRange,
        }
      })
    }

    case VariantsTableRow.Returns: {
      return transformMap(rawData, (_dateMs, qty) => ({
        value: roundQuantity(qty),
      }))
    }

    case VariantsTableRow.SalesIndex: {
      return transformMap(rawData, (dateMs, qty) => {
        const dateRange = getDateRange(dateMs)
        if (dateRange.start > startOfTodayUTC()) {
          return { value: null, dateRange }
        }

        const benchmarkEntry = config.benchmarkData?.get(dateMs)
        const current = qty == null ? 0 : Math.round(qty)
        const indexValue = calculateSalesIndex(current, benchmarkEntry?.quantity ?? null)
        if (indexValue == null) {
          return { value: null, dateRange, noComparisonData: true }
        }

        return {
          value: indexValue,
          dateRange,
          benchmarkDateRange: getDateRange(benchmarkEntry!.benchmarkMs),
          currentValue: current,
          benchmarkValue: Math.round(benchmarkEntry!.quantity!),
        }
      })
    }

    case VariantsTableRow.NetSales: {
      if (!secondaryData) {
        throw new Error("NetSales calculation requires secondaryData (returns)")
      }

      return transformMap(rawData, (dateMs, qty) => {
        const salesValue = qty ? Math.round(qty) : 0
        const returnsValue = Math.round(secondaryData.get(dateMs) ?? 0)
        const netSalesValue = salesValue - returnsValue

        const dateRange = getDateRange(dateMs)
        if (dateRange.start > startOfTodayUTC()) return { value: null, dateRange }

        return {
          value: netSalesValue !== 0 ? netSalesValue : null,
          dateRange,
        }
      })
    }

    case VariantsTableRow.PreOrders: {
      return transformMap(rawData, (dateMs, qty) => ({
        value: roundQuantity(qty),
        dateRange: getDateRange(dateMs),
      }))
    }

    case VariantsTableRow.IncomingStock: {
      return transformMap(rawData, (_dateMs, qty) => ({
        value: roundQuantity(qty),
      }))
    }

    case VariantsTableRow.ArrivedStock: {
      return transformMap(rawData, (_dateMs, qty) => ({
        value: roundQuantity(qty),
      }))
    }

    case VariantsTableRow.OptimalStock:
    case VariantsTableRow.ClosingStock: {
      const comparableDate = monthlyView ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()
      const isClosingStock = rowType === VariantsTableRow.ClosingStock

      return transformMap(rawData, (dateMs, qty) => {
        const date = new Date(dateMs)

        // Before the first known inventory record we have no stock data for this variant —
        // show a dash (value: null) instead of a misleading 0. Only ClosingStock opts in;
        // future periods are never affected (they carry projected closing stock).
        if (
          isClosingStock &&
          isBeforeFirstKnownInventory(date, comparableDate, config.firstInventoryDate)
        ) {
          return { value: null }
        }

        const stockBreakdown = isClosingStock
          ? config.closingStockBreakdowns?.get(dateMs)
          : undefined
        const value = Math.round(qty ?? 0)
        return stockBreakdown ? { value, stockBreakdown } : { value }
      })
    }

    case VariantsTableRow.WeeksOnHand: {
      if (!stockData || !forecastData) {
        throw new Error("WeeksOnHand calculation requires stockData and forecastData")
      }

      const currentWeekStart = getStartOfCurrentWeekUTC()

      // In monthly view the bucket date is startOfMonth (e.g. Apr 1) but weekly forecasts
      // live at start-of-week dates (Apr 6, 13, ...). The same-day filter inside
      // calculateWeeksOnHandForMultipleDates only excludes exact-date matches, so the
      // current week's forecast would erroneously get consumed in monthly view, inflating
      // the current-month value by ~1 week. Anchor the current month's bucket to the
      // current week's start so weekly and monthly views return the same value for the
      // current period.
      const anchoredStock = stockData.map((s) => ({
        originalDate: s.date,
        date: monthlyView && isSameMonth(s.date, currentWeekStart) ? currentWeekStart : s.date,
        quantity: s.quantity ?? 0,
      }))

      const forecastWithDefaults = forecastData.map((f) => ({
        date: f.date,
        quantity: f.quantity ?? 0,
      }))

      const weeksOnHandData = calculateWeeksOnHandForMultipleDates(
        anchoredStock,
        forecastWithDefaults,
      )

      const hasForecasts = forecastData.some((f) => (f.quantity ?? 0) > 0)
      const comparableDate = monthlyView ? getStartOfCurrentMonthUTC() : getStartOfCurrentWeekUTC()

      // Map results back to the original (month-start) dates so the cell renderer
      // can look them up by selectedDates entries.
      return new Map(
        weeksOnHandData.map((w, i): [number, ProductTableData] => {
          const originalDate = anchoredStock[i]!.originalDate

          // Before the first known inventory record we have no stock basis for this variant —
          // show a dash instead of "0 weeks" (which reads as a stockout). Mirrors ClosingStock.
          if (
            isBeforeFirstKnownInventory(originalDate, comparableDate, config.firstInventoryDate)
          ) {
            return [originalDate.getTime(), { value: null }]
          }

          return [
            originalDate.getTime(),
            {
              value: hasForecasts ? w.value : null,
              exceedsOneYear: hasForecasts && w.value === 52,
            },
          ]
        }),
      )
    }

    case VariantsTableRow.RawForecasts:
    case VariantsTableRow.Forecast: {
      return transformMap(rawData, (_dateMs, qty) => ({
        value: qty && roundForecast(qty),
      }))
    }

    case VariantsTableRow.Availability: {
      return transformMap(rawData, (_dateMs, qty) => ({
        value: roundQuantity(qty),
      }))
    }

    default:
      return transformMap(rawData, (_dateMs, qty) => ({
        value: roundQuantity(qty),
      }))
  }
}
